// 手机端使用单列输入与可换行工具栏，长地址完整阅读；电脑保留原布局，避免窄屏遮住表单或操作。
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { toast } from "../components/ui/sonner";
import { FileStack, Loader2, RefreshCw, Send, Inbox, CalendarCheck, PenLine, ClipboardList, Package, ChevronRight, MailWarning } from "lucide-react";
import { DeptBriefCard } from "../components/DeptBriefCard";
import { CdcTeamBar } from "../components/CdcTeamBar";
import { fmtD } from "../lib/date";

const SECTIONS = [
  { key: "tasks", zh: "线上项目经理待办任务卡", en: "Online PM task cards", icon: ClipboardList, hint: "引擎任务卡 role=线上项目经理 且已解锁 / 阻塞 / 等待外部" },
  { key: "comms", zh: "待发送通信草稿", en: "Comm drafts", icon: Send, hint: "AI 起草的短信 / 邮件，需人工确认发送" },
  { key: "inbound", zh: "待处理收件", en: "Inbound pending", icon: Inbox, hint: "业主 / 分包 / 市府回复待归档或应用" },
  { key: "inspections", zh: "市检预约与整改", en: "Inspections", icon: CalendarCheck, hint: "已预约待录结果 · 未通过整改中" },
  { key: "signoffs", zh: "待业主签字确认", en: "Owner sign-offs", icon: PenLine, hint: "阶段确认书 / Pre-Con 三件套 / 完工验收" },
  { key: "receipts", zh: "设计移交资料缺项", en: "Handoff gaps", icon: MailWarning, hint: "Permit 号 / 业主电话 / PM / 开工日期" },
  { key: "materials", zh: "材料逾期未下单", en: "Materials overdue", icon: Package, hint: "长周期材料超过下单期限" },
];
const STATE_ZH = { ready: "可开始", blocked: "阻塞", waiting_external: "等待外部" };
const dayCls = (d) => (d >= 14 ? "text-red-600" : d >= 5 ? "text-amber-600" : "text-slate-500");

export default function CdcWorkbench() {
  const { api, lang } = useApp();
  const zh = lang === "zh";
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sec, setSec] = useState("tasks");
  const [team, setTeam] = useState("");
  const [phase, setPhase] = useState(null);
  // 走查清单三-4（视频 10:26–13:07）：项目范围筛选 all=全部 / mine=只看我负责（后端按 co_staff 判定）
  const [scope, setScope] = useState("all");

  const load = async (t = team, sc = scope) => {
    setBusy(true);
    try {
      const params = {};
      if (t) params.team = t;
      if (sc && sc !== "all") params.scope = sc;
      const { data: d } = await api.get("/cdc/workbench", { params });
      setData(d);
    } catch (e) { toast.error(e?.detailText || e.message); setData({ sections: {}, counts: {}, denied: true }); }
    setBusy(false);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line
  const changeTeam = (t) => { setTeam(t); setPhase(null); load(t, scope); };
  const changeScope = (sc) => { setScope(sc); setPhase(null); load(team, sc); };

  if (!data) return <div className="flex justify-center py-20" data-testid="cdc-loading"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div>;
  if (data.denied) return <div className="text-center py-20 text-sm text-slate-500" data-testid="cdc-denied">{zh ? "仅线上项目经理 / 工程管理可访问" : "Online PM / construction management only"}</div>;
  const phaseOf = Object.fromEntries((data.projectList || []).map((p) => [p.id, p.phaseIdx]));
  const inPhase = (x) => phase == null || phaseOf[x.projectId] === phase;
  const counts = Object.fromEntries(Object.entries(data.sections || {}).map(([k, v]) => [k, v.filter(inPhase).length]));
  const total = Object.values(counts).reduce((s, n) => s + n, 0);
  const items = (data.sections[sec] || []).filter(inPhase);
  const phaseName = phase == null ? "" : (data.phases || []).find((p) => p.phaseIdx === phase)?.phase;
  const go = (x) => nav(x.designId && sec === "receipts" ? `/project/${x.projectId}` : `/project/${x.projectId}`);

  return (
    <div className="space-y-5" data-testid="cdc-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2"><FileStack className="h-5 w-5 text-violet-600" />{zh ? "线上项目经理工作台" : "Online PM Workbench"}</h1>
          <p className="text-xs text-slate-400 mt-0.5">{zh ? `跨 ${data.projects} 个在建项目汇总线上 / 文件 / 沟通类工作，${data.projectsWithWork} 个项目有待办 · 共 ${total} 项` : `${total} items across ${data.projectsWithWork} projects`}</p>
        </div>
        <button data-testid="cdc-refresh" onClick={() => load()} className="p-2 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}</button>
      </div>

      <CdcTeamBar data={data} zh={zh} team={team} onTeam={changeTeam} phase={phase} onPhase={setPhase} scope={scope} onScope={changeScope} />

      {/* showKickoff={false}：2026-10-14 走查清单「二、重复冗余 #12」——「待开工」只在「工程项目」页显示，本页不显示该统计徽章 */}
      <DeptBriefCard compact showKickoff={false} />

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2" data-testid="cdc-sections">
        {SECTIONS.map((s) => {
          const n = counts[s.key] || 0;
          const Icon = s.icon;
          return (
            <button key={s.key} data-testid={`cdc-sec-${s.key}`} data-count={n} onClick={() => setSec(s.key)}
              className={`rounded-xl border p-3 text-left transition-colors ${sec === s.key ? "bg-slate-900 text-white border-slate-900" : n ? "bg-white border-slate-200 hover:border-violet-300" : "bg-slate-50 border-slate-100 text-slate-400"}`}>
              <Icon className={`h-4 w-4 mb-1 ${sec === s.key ? "text-violet-300" : n ? "text-violet-600" : "text-slate-300"}`} />
              <p className="text-[11px] font-semibold leading-tight">{zh ? s.zh : s.en}</p>
              <p className="text-xl font-bold leading-tight mt-0.5">{n}</p>
            </button>
          );
        })}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid={`cdc-list-${sec}`}>
        <div className="px-4 py-2 border-b border-slate-100 flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs font-bold text-slate-700">{zh ? SECTIONS.find((s) => s.key === sec)?.zh : SECTIONS.find((s) => s.key === sec)?.en} <span className="text-slate-400 font-normal">· {items.length}</span>{phaseName && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700" data-testid="cdc-phase-active">{phaseName}</span>}</p>
          <p className="text-[10px] text-slate-400">{SECTIONS.find((s) => s.key === sec)?.hint}</p>
        </div>
        {items.length === 0 && <p className="text-center text-sm text-slate-400 py-12" data-testid="cdc-empty">{zh ? "本类暂无待办 ✓" : "Nothing pending ✓"}</p>}
        {items.map((x, i) => (
          <div key={x.taskId || x.id || `${x.projectId}-${i}`} data-testid={`cdc-item-${x.taskId || x.id || x.projectId}`} className="px-4 py-2.5 border-b border-slate-50 last:border-0 flex flex-wrap items-center gap-3 hover:bg-violet-50/30">
            <div className="w-full sm:w-auto sm:flex-1 min-w-0">
              <p className="text-xs text-slate-500 break-words sm:truncate">{x.address}</p>
              <p className="text-sm font-semibold text-slate-800 leading-snug">
                {sec === "tasks" && <>{x.title}<span className="ml-2 text-[10px] font-normal text-slate-400">{x.phase} · {STATE_ZH[x.state] || x.state}{x.requiresEvidence ? ` · 需证据 ${x.evidenceCount}` : ""}</span></>}
                {sec === "comms" && <>{x.channel?.toUpperCase()} → {x.toName || x.toRole}<span className="ml-2 text-[11px] font-normal text-slate-500">{x.subject || x.body}</span></>}
                {sec === "inbound" && <>{x.from || x.channel} · {x.kind}<span className="ml-2 text-[11px] font-normal text-slate-500">{x.summary}</span></>}
                {sec === "inspections" && <>{x.typeZh}<span className={`ml-2 text-[10px] px-1.5 rounded-full ${x.status === "rectifying" ? "bg-red-100 text-red-700" : "bg-sky-100 text-sky-700"}`}>{x.status === "rectifying" ? "未通过 · 整改中" : `已约 ${fmtD(x.scheduledDate)}`}</span>{x.riskNote && <span className="ml-2 text-[11px] font-normal text-slate-500">{x.riskNote}</span>}</>}
                {sec === "signoffs" && <>{x.title}<span className="ml-2 text-[10px] text-slate-400">{x.kind === "precon" ? "Pre-Con" : x.kind === "confirm" ? "业主确认书" : "阶段签字"}</span></>}
                {sec === "receipts" && <>{zh ? "移交资料缺：" : "Missing: "}{x.missing.join(" · ")}</>}
                {sec === "materials" && <>{x.label}<span className="ml-2 text-[10px] text-slate-400">{zh ? "应下单" : "due"} {fmtD(x.dueDate)}</span></>}
              </p>
            </div>
            <span className={`text-xs font-bold whitespace-nowrap ${dayCls(x.days || 0)}`} data-testid={`cdc-days-${x.taskId || x.id || x.projectId}`}>{x.days || 0}{zh ? " 天" : "d"}</span>
            <button data-testid={`cdc-go-${x.taskId || x.id || x.projectId}`} onClick={() => go(x)} className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-violet-700 hover:text-violet-900 whitespace-nowrap">{zh ? "去处理" : "Open"}<ChevronRight className="h-3.5 w-3.5" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
